import { initialize } from '@telnyx/video';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  EyeShowIcon,
  EyeHideIcon,
  FullscreenIcon,
  MicrophoneMutedIcon,
  MicrophoneUnmutedIcon,
  PhoneIcon,
  PaperPlaneIcon,
  CopyIcon,
} from '@rsos/assets/icons';
import { Box, Button, Loader, Text } from '@rsos/base-components';
import { ButtonBase } from '@rsos/base-components/Button/Button.styles';
import AudioTrack from '@rsos/components/capstone/VideoTo911/AudioTrack';
import MemoizedVideo from '@rsos/components/capstone/VideoTo911/Video';
import {
  CITIZEN,
  CONNECTED,
  DISCONNECTED,
  CONSTRAINTS_DEFAULT,
  PARTICIPANT_LEFT,
  STATE_CHANGED,
  STREAM_PUBLISHED,
  STREAM_UNPUBLISHED,
  SUBSCRIPTION_STARTED,
  ECC_USER,
} from '@rsos/constants/liveVideo/constants';
import useToggleFullscreen from '@rsos/hooks/useToggleFullscreen';
import useSelectedVideoRoom from '@rsos/hooks/videoTo911/useSelectedVideoRoom';
import {
  closeCard,
  LIVE_VIDEO_CARD,
} from '@rsos/reduxDataStore/src/features/mapCard/mapCardSlice';
import { useEndRoomMutation } from '@rsos/reduxDataStore/src/features/videoTo911/videoTo911Api';
import copyToClipboard from '@rsos/utils/copyToClipboard';
import isVideoTo911ShareTelnyxEnabled from '@rsos/utils/isVideoTo911ShareTelnyxEnabled';
import { captureExceptionWithScope } from '@rsos/utils/sentry';
import { notifyError, notifySuccess } from '@rsos/utils/toastUtils';
import filterStreams from '@rsos/utils/videoTo911/filterStreams';
import {
  trackEndVT911Click,
  trackEndVT911Response,
  trackEndVT911FromServer,
  trackVideoBlurUnblurClick,
  trackVideoToggleFullscreenClick,
  trackVideoMuteUnmuteClick,
} from '../../videoTo911Trackings';
import ShareLiveStreamModal from '../ShareLiveStreamModal';
import { sendRoomLink, useOpenConversationId } from '../roomLink';
import {
  BlurContainer,
  BlurOverflowContainer,
  IconWrapper,
} from './LiveVideoCardLegacy.styles';

const useTelnyxUser = () => {
  const profile = useSelector(state => state.sinatra.user.profile);
  const loggedInUser = useMemo(() => {
    const { id = '', first_name = '', last_name = '' } = profile;

    return {
      id: id ? parseInt(id) : '',
      name: `${first_name} ${last_name}`.trim(),
    };
  }, [profile]);
  return loggedInUser;
};

/**
 * Why the dispatcher can't see the room. @telnyx/video only surfaces a server
 * rejection (403 room full, 401, 503) as an `unknown` disconnect; the server's
 * message goes to the console. A rejection before the room ever connects comes
 * from a token minted seconds earlier, so it's treated as the room being full.
 */
const CONNECTION_ERRORS = {
  FULL: 'full',
  FAILED: 'failed',
  LOST: 'lost',
};

const CONNECTION_ERROR_MESSAGES = {
  [CONNECTION_ERRORS.FULL]:
    'This video session is full. It may already be open on another screen.',
  [CONNECTION_ERRORS.FAILED]: 'Unable to connect to the video call.',
  [CONNECTION_ERRORS.LOST]: 'The video connection was lost.',
};

const getConnectionError = ({ reason, hasConnected }) => {
  if (hasConnected) {
    return CONNECTION_ERRORS.LOST;
  }
  return reason === 'unknown'
    ? CONNECTION_ERRORS.FULL
    : CONNECTION_ERRORS.FAILED;
};

/**
 * How long the dispatcher waits for the caller's video before the card offers
 * to resend the link.
 */
export const CALLER_VIDEO_TIMEOUT_MS = 30 * 1000;

const disconnectQuietly = room => {
  // Teardown failures (e.g. the socket already closed) are not actionable
  Promise.resolve(room?.disconnect()).catch(() => undefined);
};

const LiveVideoCardLegacy = () => {
  const roomRef = useRef();
  const initGenerationRef = useRef(0);
  const ownStreamRef = useRef(null);
  const addedRemoteStreamRef = useRef(false);
  // The participant publishing the citizen stream. Share viewers and a stale
  // session of this dispatcher can also be in the room, so leaving alone
  // doesn't mean the caller left.
  const citizenParticipantIdRef = useRef(null);
  const citizenVideoRef = useRef();
  const [isAudioMuted, setIsAudioMuted] = useState(false);
  const [isBlurred, setIsBlurred] = useState(true);
  const [isWaitingForCitizen, setisWaitingForCitizen] = useState(true);
  const [hasWaitTimedOut, setHasWaitTimedOut] = useState(false);
  const [hasCallerLeft, setHasCallerLeft] = useState(false);
  const [isRoomDisconnected, setIsRoomDisconnected] = useState(false);
  const connectionErrorRef = useRef(null);
  const [connectionError, setConnectionError] = useState(null);
  const [ownStream, setOwnStream] = useState(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  const selectedCallerId = useSelector(
    state => state.emergencyCalls.selectedCallerID,
  );
  const accountId = useSelector(state => state.psaps?.currentPSAP?.account_id);
  const selectedVideoRoom = useSelectedVideoRoom(selectedCallerId);
  const openConversationId = useOpenConversationId(selectedCallerId);

  const psaps = useSelector(state => state.psaps);
  const videoShareEnabled = isVideoTo911ShareTelnyxEnabled(psaps);

  const telnyxUser = useTelnyxUser();

  const [fullscreenRef, toggleFullscreen, isFullscreen] = useToggleFullscreen();

  const dispatch = useDispatch();
  const [triggerEndRoom] = useEndRoomMutation();

  const captureVideoError = useCallback(
    (error, tags = {}) => {
      captureExceptionWithScope(
        error,
        {
          roomid: selectedVideoRoom?.room_id,
          clientTokenExpiry: selectedVideoRoom?.client_token_expiry,
          refreshTokenExpiry: selectedVideoRoom?.refresh_token_expiry,
        },
        undefined,
        { roomId: selectedVideoRoom?.room_id ?? 'unknown', ...tags },
      );
    },
    [selectedVideoRoom],
  );

  const shareUrl = useMemo(() => {
    if (!selectedVideoRoom?.room_id || !selectedVideoRoom?.refresh_token) {
      return null;
    }
    return `${window.location.protocol}//${window.location.host}/live-video/watch/${selectedVideoRoom.room_id}?refresh_token=${selectedVideoRoom.refresh_token}`;
  }, [selectedVideoRoom]);

  const handleCopyLink = useCallback(() => {
    if (shareUrl) {
      copyToClipboard(shareUrl);
      notifySuccess('Link copied to clipboard');
    }
  }, [shareUrl]);

  /**
   * Texts the caller the same room link again and restarts the wait
   */
  const handleResendLink = useCallback(async () => {
    if (!openConversationId) {
      notifyError(
        "Couldn't resend the link. Start a chat with the caller and try again.",
      );
      return;
    }

    try {
      await sendRoomLink({
        conversationId: openConversationId,
        roomId: selectedVideoRoom?.room_id,
        refreshToken: selectedVideoRoom?.refresh_token,
      });
      setHasWaitTimedOut(false);
      setHasCallerLeft(false);
    } catch (error) {
      captureVideoError(error);
      notifyError("Couldn't resend the link. Please try again.");
    }
  }, [captureVideoError, openConversationId, selectedVideoRoom]);

  const handleBlur = useCallback(() => {
    setIsBlurred(prev => {
      const updateState = !prev;
      trackVideoBlurUnblurClick({
        blurVideo: updateState,
        roomId: selectedVideoRoom?.room_id,
      });
      return updateState;
    });
  }, [selectedVideoRoom]);

  const handleToggleFullscreen = useCallback(() => {
    trackVideoToggleFullscreenClick({
      triggerFullscreen: !isFullscreen,
      roomId: selectedVideoRoom?.room_id,
    });
    toggleFullscreen();
  }, [toggleFullscreen, isFullscreen, selectedVideoRoom]);

  const handleEndStream = useCallback(async () => {
    // Track that the user clicked to end the VT911 session
    trackEndVT911Click({
      roomId: selectedVideoRoom?.room_id,
    });

    triggerEndRoom({
      accountId,
      callerId: selectedCallerId,
      callStartTime: selectedVideoRoom?.callStartTime,
      roomId: selectedVideoRoom?.room_id,
    }).then(async response => {
      await roomRef.current?.disconnect();
      roomRef.current = null;
      citizenVideoRef.current = null;

      // Track the server's response to ending the VT911 session
      trackEndVT911Response({
        responseSuccess: !response?.error,
        roomId: selectedVideoRoom?.room_id,
      });

      if (response.error) {
        captureVideoError(response.error);
      }

      // The room is gone either way (the slice drops it even when the request
      // fails), so there's nothing left for the card to do. Starting again
      // creates a fresh room. Unmounting also releases the dispatcher's mic.
      dispatch(closeCard(LIVE_VIDEO_CARD));
    });
  }, [
    accountId,
    captureVideoError,
    dispatch,
    selectedCallerId,
    selectedVideoRoom,
    triggerEndRoom,
  ]);

  const handleToggleMuteUnmute = useCallback(async () => {
    try {
      const room = roomRef.current;
      const roomState = room.getState();
      const localParticipantId = roomState.localParticipantId;
      const ownStream = await room.getParticipantStream(
        localParticipantId,
        ECC_USER,
      );
      const navigatorStream = await navigator?.mediaDevices?.getUserMedia({
        ...CONSTRAINTS_DEFAULT,
        video: false,
      });
      const audioStream = navigatorStream.getAudioTracks()[0];

      room.updateStream(ECC_USER, {
        audio: ownStream.isAudioEnabled ? null : audioStream,
        video: null,
      });

      setIsAudioMuted(prev => {
        const updateState = !prev;
        trackVideoMuteUnmuteClick({
          muteVideo: updateState,
          roomId: selectedVideoRoom?.room_id,
        });
        return updateState;
      });
    } catch (error) {
      captureVideoError(error);
    }
  }, [captureVideoError, selectedVideoRoom]);

  /**
   * Handle adding user's own audio stream to the room. Invoked when room is in
   * a CONNECTED state
   */
  const handleOwnStream = useCallback(
    // eslint-disable-next-line no-unused-vars
    async state => {
      try {
        const room = roomRef.current;
        // Handle audio stream from user
        const stream = await navigator?.mediaDevices?.getUserMedia({
          ...CONSTRAINTS_DEFAULT,
          video: false,
        });

        // The room was torn down while the mic permission was pending
        if (roomRef.current !== room) {
          stream.getTracks().forEach(track => track.stop());
          return;
        }
        ownStreamRef.current = stream;

        const audioStream = stream.getAudioTracks()[0];

        await room.addStream(ECC_USER, {
          audio: audioStream,
          video: null,
        });

        setOwnStream(stream);

        // When starting the current user's stream (audio only), mute self.
        handleToggleMuteUnmute();
      } catch (error) {
        captureVideoError(error);
      }
    },
    [captureVideoError, handleToggleMuteUnmute],
  );

  /**
   * Handles adding a remote stream from an external user. Only a max of 2
   * participants are allowed in a room. The current user and the external user.
   * Currently only one video is supported
   * @param {Instance} state - The telnyx video stream state
   * @param {Object} participant - the participant of the stream
   */
  const handleRemoteStream = useCallback(
    async ({ state, participant }) => {
      const room = roomRef.current;
      try {
        if (room) {
          const participantId = participant.id;
          const localParticipantId = state.localParticipantId;
          if (
            participantId &&
            localParticipantId &&
            participantId !== localParticipantId &&
            !addedRemoteStreamRef.current
          ) {
            const participantStream = room.getParticipantStream(
              participantId,
              CITIZEN,
            );
            // External user hasn't fully connected yet
            if (!participantStream) {
              return;
            }

            addedRemoteStreamRef.current = true;
            citizenParticipantIdRef.current = participantId;

            await room.addSubscription(
              participantId,
              CITIZEN,
              CONSTRAINTS_DEFAULT,
            );

            // Note: participantStream and remoteStream appear to be the same.
            // They are not the same due to the subscription being added
            const remoteStream = room.getParticipantStream(
              participantId,
              CITIZEN,
            );

            const remoteMediaStream = new MediaStream([
              remoteStream.audioTrack,
              remoteStream.videoTrack,
            ]);
            citizenVideoRef.current.srcObject = remoteMediaStream;
          }
        }
      } catch (error) {
        captureVideoError(error);
      }
    },
    [captureVideoError],
  );

  const handleRoomDisconnected = useCallback(() => {
    setIsRoomDisconnected(true);
  }, []);

  /**
   * The caller left the room (tapped End, or Telnyx timed out their dropped
   * connection) or removed their stream (an abandoned publish on the caller
   * page). The two look the same from here, so the card goes back to waiting
   * and offers to resend the link rather than showing ended tracks as black.
   */
  const handleCallerVideoEnded = useCallback(() => {
    citizenParticipantIdRef.current = null;
    addedRemoteStreamRef.current = false;
    if (citizenVideoRef.current) {
      citizenVideoRef.current.srcObject = null;
    }
    setisWaitingForCitizen(true);
    setHasWaitTimedOut(false);
    setHasCallerLeft(true);
  }, []);

  const telnyxStateCallback = useCallback(
    async state => {
      try {
        if (state.status !== DISCONNECTED) {
          // TODO: timed_out is also possible now
          const streamsCount = state.streams.size;
          const filteredStreams = filterStreams(state.streams, ECC_USER);
          // Assume only one citizen can join
          const foundStream = filteredStreams.find(stream => {
            return stream.key === CITIZEN;
          });

          if (streamsCount > 0 && !!foundStream) {
            setisWaitingForCitizen(false);
            setHasCallerLeft(false);
          }
        }
      } catch (error) {
        captureVideoError(error);
      }
    },
    [captureVideoError],
  );

  const initializeTelnyxVideo = useCallback(async () => {
    const generation = ++initGenerationRef.current;
    const roomId = selectedVideoRoom?.room_id;
    const clientToken = selectedVideoRoom?.client_token;
    const context = JSON.stringify(telnyxUser);

    let room;
    try {
      room = await initialize({
        roomId,
        clientToken,
        context,
      });
    } catch (error) {
      if (generation === initGenerationRef.current) {
        connectionErrorRef.current = CONNECTION_ERRORS.FAILED;
        captureVideoError(error, {
          connection_error: CONNECTION_ERRORS.FAILED,
        });
        setConnectionError(CONNECTION_ERRORS.FAILED);
      }
      return;
    }

    // The card closed or retried while this room was initializing
    if (generation !== initGenerationRef.current) {
      disconnectQuietly(room);
      return;
    }

    roomRef.current = room;
    const isCurrentRoom = () => roomRef.current === room;
    let hasConnected = false;

    // The first failure wins so a later timeout can't mask a rejection
    const failConnection = (error, reason) => {
      if (connectionErrorRef.current) {
        return;
      }
      const kind = getConnectionError({ reason, hasConnected });
      connectionErrorRef.current = kind;
      captureVideoError(error, {
        connection_error: kind,
        disconnect_reason: String(reason),
      });
      setConnectionError(kind);
    };

    room.on(STATE_CHANGED, state => {
      if (isCurrentRoom()) {
        telnyxStateCallback(state);
      }
    });

    room.on(CONNECTED, async state => {
      if (!isCurrentRoom()) {
        return;
      }
      hasConnected = true;
      const participants = state?.participants;
      const participtantsSize = state?.participants?.size;

      // Handle the video stream from the room if the user refreshes the
      // page or this component is unmounted and remounted
      if (participtantsSize > 1) {
        /**
         * Array.from returns a tuple with the participant ID and participant
         * data. The participant ID is unused. Filter out the current user from
         * the list of participants due to Telnyx keeping track of the current
         * user through page refresh which results in the user being a `remote`
         * user
         */
        const filteredParticipants = Array.from(participants).filter(
          participant => {
            return participant?.[1].context !== JSON.stringify(telnyxUser);
          },
        );
        for (const [, participant] of filteredParticipants) {
          await handleRemoteStream({ state, participant });
        }
      }

      // Handle audio stream from user
      handleOwnStream(state);
    });

    // A stream has been published to the room
    room.on(STREAM_PUBLISHED, async (participantId, streamKey, state) => {
      if (!isCurrentRoom()) {
        return;
      }
      // Ignore streams from the current user
      const participant = state.participants.get(participantId);
      if (participant.origin !== 'local') {
        if (streamKey === CITIZEN) {
          citizenParticipantIdRef.current = participantId;
        }
        // the remote stream is identified using the participantId and streamKey
        // you need to subscribe to a remote stream in order to access it's `MediaStreamTrack`s
        await room.addSubscription(
          participantId,
          streamKey,
          CONSTRAINTS_DEFAULT,
        );
      }
    });

    // A subscription to a remote stream has started
    room.on(SUBSCRIPTION_STARTED, (participantId, streamKey, state) => {
      if (!isCurrentRoom()) {
        return;
      }
      const participant = state.participants.get(participantId);
      // Ignore streams from the current user
      if (participant.origin !== 'local') {
        const remoteStream = room.getParticipantStream(
          participantId,
          streamKey,
        );

        const remoteMediaStream = new MediaStream([
          remoteStream.audioTrack,
          remoteStream.videoTrack,
        ]);

        // there is a chance that `citizenVideoRef.current` is null if citizen joins first
        if (citizenVideoRef.current) {
          citizenVideoRef.current.srcObject = remoteMediaStream;
        }
      }
    });

    room.on(STREAM_UNPUBLISHED, (participantId, streamKey) => {
      if (
        isCurrentRoom() &&
        streamKey === CITIZEN &&
        participantId === citizenParticipantIdRef.current
      ) {
        handleCallerVideoEnded();
      }
    });

    // Fires the same way whether the caller tapped End or lost signal; a
    // dropped connection just arrives later, once Telnyx times them out
    room.on(PARTICIPANT_LEFT, participantId => {
      if (
        isCurrentRoom() &&
        participantId === citizenParticipantIdRef.current
      ) {
        handleCallerVideoEnded();
      }
    });

    room.on(DISCONNECTED, reason => {
      if (!isCurrentRoom()) {
        return;
      }
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console -- dev-only: not reached in production
        console.log('disconnected', reason);
      }

      trackEndVT911FromServer({
        roomId: selectedVideoRoom?.room_id,
      });

      // user_initiated -- User clicks end call. Ends room session
      if (reason === 'user_initiated') {
        handleRoomDisconnected();
      } else {
        failConnection(new Error(`Video room disconnected: ${reason}`), reason);
      }
    });

    room
      .connect()
      .then(() => {
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console -- dev-only: not reached in production
          console.log('connected');
        }
      })
      .catch(error => {
        if (isCurrentRoom()) {
          failConnection(error, error);
        }
      });
  }, [
    captureVideoError,
    handleCallerVideoEnded,
    handleOwnStream,
    handleRemoteStream,
    handleRoomDisconnected,
    selectedVideoRoom,
    telnyxStateCallback,
    telnyxUser,
  ]);

  /**
   * Disconnects the current room and releases the dispatcher's mic. Also
   * invalidates any room still initializing so it disconnects on arrival.
   */
  const teardownRoom = useCallback(() => {
    initGenerationRef.current += 1;
    const room = roomRef.current;
    roomRef.current = null;
    disconnectQuietly(room);
    ownStreamRef.current?.getTracks().forEach(track => track.stop());
    ownStreamRef.current = null;
  }, []);

  const handleRetryConnection = useCallback(() => {
    teardownRoom();
    addedRemoteStreamRef.current = false;
    citizenParticipantIdRef.current = null;
    connectionErrorRef.current = null;
    setConnectionError(null);
    setisWaitingForCitizen(true);
    setHasWaitTimedOut(false);
    setHasCallerLeft(false);
    initializeTelnyxVideo();
  }, [initializeTelnyxVideo, teardownRoom]);

  useEffect(() => {
    initializeTelnyxVideo();
    return teardownRoom;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (roomRef.current && selectedVideoRoom?.client_token) {
      Promise.resolve(
        roomRef.current.updateClientToken(selectedVideoRoom.client_token),
      ).catch(captureVideoError);
    }
  }, [captureVideoError, selectedVideoRoom]);

  // Times the wait for the caller's video. Starts over whenever the wait does:
  // after a resend, or after a retry clears a connection error. Doesn't run
  // while the card already says the caller's video ended.
  useEffect(() => {
    if (
      !isWaitingForCitizen ||
      hasWaitTimedOut ||
      hasCallerLeft ||
      connectionError ||
      isRoomDisconnected
    ) {
      return;
    }
    const timeoutId = setTimeout(
      () => setHasWaitTimedOut(true),
      CALLER_VIDEO_TIMEOUT_MS,
    );
    return () => clearTimeout(timeoutId);
  }, [
    connectionError,
    hasCallerLeft,
    hasWaitTimedOut,
    isRoomDisconnected,
    isWaitingForCitizen,
  ]);

  const resendPrompt = hasCallerLeft
    ? "The caller's video ended."
    : hasWaitTimedOut
      ? "Caller hasn't started video yet."
      : null;

  return (
    <Box bg="background" minHeight="300px" py="16px" ref={fullscreenRef}>
      <Box
        margin={isFullscreen ? 'auto' : ''}
        justify="center"
        maxHeight="100vh"
        maxWidth="800px"
        overflow={isFullscreen ? 'auto' : ''}
        width="100%"
      >
        <Box
          horizontal
          justify="space-between"
          paddingLeft="16px"
          paddingRight="40px"
          paddingBottom="8px"
        >
          <Text color="primaryText" fontWeight="bold">
            Video Call
          </Text>
          <Box horizontal gridColumnGap="12px">
            <Box color="primaryText" cursor="pointer" onClick={handleBlur}>
              {isBlurred ? (
                <EyeHideIcon
                  width="16px"
                  height="16px"
                  viewboxHeight={16}
                  viewboxWidth={16}
                />
              ) : (
                <EyeShowIcon
                  width="16px"
                  height="16px"
                  viewboxHeight={16}
                  viewboxWidth={16}
                />
              )}
            </Box>
            <Box cursor="pointer" onClick={handleToggleFullscreen}>
              <FullscreenIcon color="primaryText" height="12px" width="12px" />
            </Box>
          </Box>
        </Box>

        {!isRoomDisconnected ? (
          <>
            {connectionError ? (
              <Box
                align="center"
                justify="center"
                height="240px"
                px="16px"
                gridRowGap="12px"
              >
                <Text color="lightDanger" textAlign="center">
                  {CONNECTION_ERROR_MESSAGES[connectionError]}
                </Text>
                <Button
                  btnType="secondary"
                  dataName="live-video-retry-button"
                  onClick={handleRetryConnection}
                >
                  Retry
                </Button>
              </Box>
            ) : (
              isWaitingForCitizen &&
              (resendPrompt ? (
                <Box
                  align="center"
                  justify="center"
                  height="240px"
                  px="16px"
                  gridRowGap="12px"
                >
                  <Text color="primaryText" textAlign="center">
                    {resendPrompt}
                  </Text>
                  <Button
                    btnType="secondary"
                    dataName="live-video-resend-link-button"
                    onClick={handleResendLink}
                  >
                    Resend Link
                  </Button>
                </Box>
              ) : (
                <Box align="center" justify="center" height="240px">
                  <Loader fluid text="Waiting for citizen..." />
                </Box>
              ))
            )}
          </>
        ) : (
          <Box align="center" justify="center" height="240px" px="16px">
            <Text color="lightDanger" textAlign="center">
              Stream has been disconnected.
            </Text>
          </Box>
        )}

        <BlurOverflowContainer>
          <BlurContainer
            align="center"
            justify="center"
            display={
              isWaitingForCitizen || isRoomDisconnected || connectionError
                ? 'none'
                : 'flex'
            }
            isBlurred={isBlurred}
          >
            <MemoizedVideo
              ref={citizenVideoRef}
              autoPlay
              onContextMenu={e => {
                e.preventDefault(); // Prevent right clicks
              }}
              style={{ maxHeight: '600px', width: '100%' }}
            />

            <AudioTrack stream={ownStream} autoMute />
          </BlurContainer>
        </BlurOverflowContainer>

        {!isWaitingForCitizen && !isRoomDisconnected && (
          <>
            <Box align="center" horizontal justify="center">
              <ButtonBase btnType="tertiary" onClick={handleToggleMuteUnmute}>
                {!isAudioMuted ? (
                  <MicrophoneUnmutedIcon color="secondaryText" />
                ) : (
                  <MicrophoneMutedIcon color="lightDanger" />
                )}
              </ButtonBase>
            </Box>
          </>
        )}

        <Box align="center" justify="center" gridRowGap="8px">
          <Button
            btnType="warning_tertiary"
            disabled={isRoomDisconnected}
            onClick={handleEndStream}
          >
            <PhoneIcon />
            End Call
          </Button>
          {videoShareEnabled && (
            <Box horizontal columnGap="8px">
              <Button
                btnType="secondary"
                disabled={!shareUrl}
                onClick={() => setIsShareModalOpen(true)}
              >
                <IconWrapper>
                  <PaperPlaneIcon />
                </IconWrapper>
                Share via SMS
              </Button>
              <Button
                btnType="secondary"
                onClick={handleCopyLink}
                disabled={!shareUrl}
              >
                <IconWrapper>
                  <CopyIcon />
                </IconWrapper>
                Copy Link
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      <ShareLiveStreamModal
        isOpened={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        roomId={selectedVideoRoom?.room_id}
        refreshToken={selectedVideoRoom?.refresh_token}
        shareUrl={shareUrl}
      />
    </Box>
  );
};

export default React.memo(LiveVideoCardLegacy);
